import * as yup from "yup";
import { formatCurrencyNoDecimal } from "../utilities/numericFormat.js";
import { Application } from "../config/const/enums.js";

export function buildPolicyOutcomeSchema(person, application) {
	return {
		...policyOutcomeValidation(person),
		...buildAcceptTermsAndConditions(),
		...(application === Application.CONTACT_CENTER ? buildUpgradePolicy() : {}),
	};
}

// Life cover
const LIFE_COVER_REQUIRED = "Life Insurance cover is required";

// Critical illness cover
const CRITICAL_ILLNESS_COVER_SELECTED =
	"Critical Illness cannot be selected without Life Cover";
const CRITICAL_ILLNESS_COVER_EXCEED =
	"Critical Illness amount cannot exceed Life cover amount";

const policyOutcomeValidation = (person) => {
	const personDetails = person;
	const lifeVal = `${person}_lifeCover`;
	const lifeCover = `${person}_lifeCover`;

	return {
		[`${person}_lifeCover`]: yup
			.number()
			.transform((value) => (isNaN(value) ? undefined : value))
			.nullable()
			.required(LIFE_COVER_REQUIRED)
			.min(
				yup.ref("$" + personDetails + "_minLifeCover"),
				({ min }) => `Minimum cover required is ${formatCurrencyNoDecimal(min)}`
			)
			.max(
				yup.ref("$" + personDetails + "_maxLifeCover"),
				({ max }) => `Maximum cover required is ${formatCurrencyNoDecimal(max)}`
			),
		[`${person}_illnessCover`]: yup
			.number()
			.transform((value) => (isNaN(value) ? undefined : value))
			.nullable()
			.test("life-cover", CRITICAL_ILLNESS_COVER_SELECTED, function (value) {
				//when return is false then the error message is triggered
				return value == null || this.parent[lifeCover] ? true : false;
			})
			.test(
				"criticalIllness-exceed",
				CRITICAL_ILLNESS_COVER_EXCEED,
				function (value) {
					//when return is false then the error message is triggered
					return value == null ? true : value <= this.parent[lifeCover];
				}
			)
			.min(
				yup.ref("$" + personDetails + "_minCICover"),
				({ min }) => `Minimum cover required is ${formatCurrencyNoDecimal(min)}`
			)
			.max(
				yup.ref("$" + personDetails + "_maxCICover"),
				({ max }) => `Maximum cover required is ${formatCurrencyNoDecimal(max)}`
			),
		[`${person}_traumaCover`]: yup
			.string()
			.when("$validateOutcomeTraumaQuestion", {
				is: true,
				then: yup
					.string()
					.oneOf(["yes", "no"])
					.required("You must answer the Total cover question"),
				otherwise: yup.string().nullable().notRequired(),
			})
			.test(
				"trauma-cover",
				"You have exceeded the $2 million maximum amount of Trauma and Critical Illness cover available across all your policies.",
				function (value) {
					//when return is false then the error message is triggered
					return value == null ? true : value === "yes" ? false : true;
				}
			),
		[`${person}_permanentDisabilityCover`]: yup
			.number()
			.transform((value) => (isNaN(value) ? undefined : value))
			.nullable()
			.test(
				"permanent-disability-cover",
				"Permanent disability cannot be selected without Life Cover",
				function (value) {
					//* when return is false then the error message is triggered
					return value == null || this.parent[lifeVal] ? true : false;
				}
			)
			.test(
				"permanent-disability-exceed",
				"Permanent disability amount cannot exceed Life cover amount",
				function (value) {
					//when return is false then the error message is triggered
					return value == null ? true : value <= this.parent[lifeVal];
				}
			)
			.min(
				yup.ref("$" + personDetails + "_minPUWCover"),
				({ min }) => `Minimum cover required is ${formatCurrencyNoDecimal(min)}`
			)
			.max(
				yup.ref("$" + personDetails + "_maxPUWCover"),
				({ max }) => `Maximum cover required is ${formatCurrencyNoDecimal(max)}`
			),
	};
};

//* T&Cs are only required on Select cover and not Quote Summary
const buildAcceptTermsAndConditions = () => {
	return {
		[`acceptOffer`]: yup.boolean().when("$isShowApplyCheckbox", {
			is: true,
			then: yup
				.boolean()
				.oneOf(
					[true],
					`You haven’t accepted the Special Terms and Conditions. You must accept these terms if you wish to include this cover in your policy. If you wish to remove this cover from your application, you can do so by deselecting the cover(s).`
				)
				.required(null),
			otherwise: yup.boolean().notRequired(),
		}),
	};
};

//* Only build this if it's the CC flow.
const buildUpgradePolicy = () => {
	return {
		[`existingPolicyNumber`]: yup.string().when("$upgradeChecked", {
			is: true,
			then: yup.string().required("A valid Policy number is required"),
			otherwise: yup.string().nullable().notRequired(),
		}),
	};
};

export function schemaPolicyOutcome(person, application) {
	return yup.object().shape({
		...buildPolicyOutcomeSchema(person, application),
	});
}
